import type { Diary, LogWarningLevel } from '../types';
import * as farmApi from '../api/farm';
import { KeyedAsyncCache } from './asyncCache';
import { refreshScheduleEventsForSeason } from './schedule';

const WARNING_LEVEL_TO_FRONTEND: Record<farmApi.FarmLogWarningLevel, LogWarningLevel> = {
  NONE: 'none',
  CAUTION: 'caution',
  CHECK: 'check',
  DANGER: 'danger',
};

/** Backend `logType` is a strict 7-value enum with no free-text field; the mock UI's
 * `hoatDong` (activity title) is user-typed free text with no fixed vocabulary. We
 * store `hoatDong` as the first line of `content` (round-tripped losslessly) and
 * infer `logType` from keywords for the coarse server-side categorization — good
 * enough for the event payload's `logType`, not meant to be authoritative. */
const KEYWORD_TO_TYPE: [RegExp, farmApi.FarmLogType][] = [
  [/gieo|trồng|giống|xuống giống/i, 'PLANTING'],
  [/phân|bón/i, 'FERTILIZING'],
  [/thuốc|sâu|bệnh|phun/i, 'PESTICIDE'],
  [/tưới|nước/i, 'IRRIGATION'],
  [/thu hoạch/i, 'HARVEST'],
  [/chi phí|mua vật tư|thanh toán/i, 'EXPENSE'],
];
function inferLogType(hoatDong: string): farmApi.FarmLogType {
  for (const [re, type] of KEYWORD_TO_TYPE) {
    if (re.test(hoatDong)) return type;
  }
  return 'OTHER';
}

function splitContent(content: string): { hoatDong: string; chiTiet: string } {
  const idx = content.indexOf('\n');
  if (idx === -1) return { hoatDong: content, chiTiet: '' };
  return { hoatDong: content.slice(0, idx), chiTiet: content.slice(idx + 1) };
}
function joinContent(hoatDong: string, chiTiet: string): string {
  return chiTiet ? `${hoatDong}\n${chiTiet}` : hoatDong;
}

function toFrontendDiary(log: farmApi.BackendFarmLog): Diary {
  const { hoatDong, chiTiet } = splitContent(log.content);
  return {
    id: log._id,
    farmerId: log.farmerId,
    fieldId: log.fieldId,
    seasonId: log.seasonId,
    ngay: log.recordedAt.slice(0, 10),
    hoatDong,
    chiTiet,
    materialsNote: log.materialsNote,
    materials: log.materials,
    laborHours: log.laborHours,
    issueNote: log.issueNote,
    issueSeverity: log.issueSeverity,
    attachments: log.attachments,
    videoUrl: log.videoUrl,
    ghiAmScript: log.voiceScript,
    ghiChuHTX: log.htxNote,
    warningLevel: WARNING_LEVEL_TO_FRONTEND[log.warningLevel],
    warningTags: log.warningTags,
    isDeleted: log.isDeleted,
    deletedReason: log.deletedReason,
    deletedAt: log.deletedAt,
    editedAt: log.editedAt,
    editCount: log.editCount,
  };
}

const byFarmer = new KeyedAsyncCache<Diary[]>(
  async (farmerId) => (await farmApi.listFarmLogsByFarmer(farmerId)).map(toFrontendDiary),
  [],
  'farmLogs:byFarmer',
);

export function getLogsByFarmer(farmerId?: string): Diary[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

function refresh(farmerId: string) {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}

export async function addLog(data: Omit<Diary, 'id'>): Promise<Diary> {
  const created = await farmApi.createFarmLog({
    farmerId: data.farmerId,
    seasonId: data.seasonId,
    fieldId: data.fieldId,
    scheduleEventId: data.scheduleEventId,
    logType: inferLogType(data.hoatDong),
    content: joinContent(data.hoatDong, data.chiTiet),
    materialsNote: data.materialsNote,
    materials: data.materials,
    laborHours: data.laborHours,
    issueNote: data.issueNote,
    issueSeverity: data.issueSeverity,
    attachments: data.attachments,
    videoUrl: data.videoUrl,
    voiceScript: data.ghiAmScript,
  });
  refresh(data.farmerId);
  // A log can flip a ScheduleEvent to DONE (explicit pick or automatic
  // nearest-match reconciliation) — refresh the season's schedule so the
  // step-picker no longer offers an already-fulfilled step.
  if (data.seasonId) refreshScheduleEventsForSeason(data.seasonId);
  return toFrontendDiary(created);
}

export async function updateLog(id: string, data: Partial<Diary>, farmerId: string): Promise<Diary> {
  const payload: farmApi.UpdateFarmLogPayload = {
    materialsNote: data.materialsNote,
    materials: data.materials,
    laborHours: data.laborHours,
    issueNote: data.issueNote,
    issueSeverity: data.issueSeverity,
    attachments: data.attachments,
  };
  if (data.hoatDong !== undefined || data.chiTiet !== undefined) {
    // Need both halves to rebuild `content` — caller is expected to pass the full
    // updated Diary fields it already has in hand (see farmer/log/page.tsx).
    const current = byFarmer.get(farmerId).find((d) => d.id === id);
    const hoatDong = data.hoatDong ?? current?.hoatDong ?? '';
    const chiTiet = data.chiTiet ?? current?.chiTiet ?? '';
    payload.content = joinContent(hoatDong, chiTiet);
    payload.logType = inferLogType(hoatDong);
  }
  const updated = await farmApi.updateFarmLog(id, payload);
  refresh(farmerId);
  return toFrontendDiary(updated);
}

export async function softDeleteLog(id: string, reason: string, farmerId: string): Promise<Diary> {
  const deleted = await farmApi.deleteFarmLog(id, reason);
  refresh(farmerId);
  return toFrontendDiary(deleted);
}

export async function setLogHtxNote(id: string, note: string, farmerId: string): Promise<Diary> {
  const updated = await farmApi.setFarmLogHtxNote(id, note);
  refresh(farmerId);
  return toFrontendDiary(updated);
}
